import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { capitalize } from 'utils';
import { Formik, Field } from 'formik';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { SkusActions } from 'state/modules/skus';
import { PricingTypesSelect } from 'components/AsyncFields';
import { TextField, MaterialSelectField } from 'components/FormFields';
import { INPUT_REGEX } from 'constants';

const ContainerBox = styled(Box)({
  width: '100%',
});

const FormContainer = styled('form')({
  width: '50%',
});

const MtGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2.5), // 20px
}));

const SkuBasicInfo = ({ formRef, step, accounting, exit }) => {
  const dispatch = useDispatch();
  const [priceUnitError, setPriceUnitError] = useState('');

  const initialValues = {
    tax_type: step?.tax_type || '',
    name: step?.name || '',
    description: step?.description || '',
    notes: step?.notes || '',
    price_unit: step?.price_unit || '',
  };

  const formSchema = Yup.object().shape({
    tax_type: Yup.object().required(i18n.t('common.required_field')),
    name: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string()
      .required(i18n.t('common.required_field'))
      .matches(INPUT_REGEX, i18n.t('common.input_check')),
    notes: Yup.string().max(
      255,
      i18n.t('common.max_long', {
        max_long: 255,
      })
    ),
    price_unit: Yup.string(),
  });

  const onSubmit = (values) => {
    let data = { ...values };
    if (values?.tax_type?.allowed_unit_types?.length && !values?.price_unit) {
      setPriceUnitError(i18n.t('common.required_field'));
      return false;
    }

    setPriceUnitError('');

    Object.keys(data).forEach((key) => !data[key] && delete data[key]);

    dispatch(SkusActions.fetchCreateSku(data, 'details', exit));
  };

  return (
    <ContainerBox>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            enableReinitialize={true}
            innerRef={formRef}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, errors, touched, setFieldValue }) => (
              <FormContainer noValidate onSubmit={handleSubmit}>
                <MtGrid size={{ xs: 12 }}>
                  <Field
                    errors={errors}
                    touched={touched}
                    name="tax_type"
                    multiple={false}
                    margin="none"
                    label={i18n.t('skus.wizard.type')}
                    customParams={{
                      charge_type: ['REVENUE', 'HOLD'].includes(accounting?.accounting_type)
                        ? 'revenue'
                        : 'discount',
                    }}
                    value={values.accounting_type}
                    setFieldValue={setFieldValue}
                    component={PricingTypesSelect}
                  />
                </MtGrid>
                <MtGrid size={{ xs: 12 }}>
                  <Field
                    name="name"
                    label={i18n.t('common.name')}
                    margin={'none'}
                    component={TextField}
                  />
                </MtGrid>
                <MtGrid size={{ xs: 12 }}>
                  <Field
                    name="description"
                    label={i18n.t('skus.wizard.invoice_description')}
                    margin={'none'}
                    component={TextField}
                  />
                </MtGrid>
                <MtGrid size={{ xs: 12 }}>
                  <Field
                    name="notes"
                    label={i18n.t('skus.wizard.invoice_note')}
                    margin={'none'}
                    required={false}
                    component={TextField}
                  />
                </MtGrid>
                {values?.tax_type?.allowed_unit_types?.length > 0 && (
                  <MtGrid size={{ xs: 6 }}>
                    <Field
                      asyncErrors={priceUnitError}
                      name="price_unit"
                      data-cy="price-unit"
                      component={MaterialSelectField}
                      label={i18n.t('telemetry.units')}
                      options={
                        values?.tax_type?.allowed_unit_types?.map((val) => {
                          return {
                            value: val,
                            label: capitalize(val?.toLowerCase()),
                          };
                        }) || []
                      }
                      placeholder={i18n.t('telemetry.units')}
                    />
                  </MtGrid>
                )}
              </FormContainer>
            )}
          </Formik>
        </Grid>
      </Container>
    </ContainerBox>
  );
};

SkuBasicInfo.propTypes = {
  formRef: PropTypes.any,
  step: PropTypes.object,
  accounting: PropTypes.bool,
  exit: PropTypes.func,
};

export default SkuBasicInfo;
